/**
 * Client-side preflight checks for workflow_steps (mirrors backend publish rules).
 * See backend/docs/dev/workflow_json_contract_v1.md
 */

import { buildEdgeId } from './flowEdgeUtils';

/** Human-readable fallbacks when backend message is absent */
const VIOLATION_MESSAGES = {
  WF_STEPS_NOT_ARRAY: 'workflow_steps must be an array.',
  WF_STEPS_EMPTY: 'Add at least one step, including a step with id "start".',
  WF_START_MISSING: 'A step with id "start" is required.',
  WF_START_DUPLICATE: 'Only one step may have id "start".',
  WF_DUPLICATE_STEP_ID: 'Each step id must be unique.',
  WF_STEP_INVALID_ID: 'Every step must have a non-empty id.',
  WF_STEP_ID_INVALID_FORMAT: 'Step id must start with a letter and use only letters, numbers, underscores, or hyphens.',
  WF_STEP_UNKNOWN_TYPE: 'Unknown step type.',
  WF_NAV_INVALID_LINK: 'A navigation action is missing a valid link.',
  WF_NAV_UNKNOWN_TARGET: 'A navigation link points to a step that does not exist.',
  WF_UNREACHABLE_STEP: 'This step is not reachable from start.',
  WF_FORM_MISSING_SLUG: 'Form steps must have a non-empty slug.',
  WF_FORM_UNKNOWN_SLUG: 'This form slug is not defined for your organization.',
  WF_FORM_LEGACY_ID: 'Form step has an unresolvable form_definition_id.',
  WF_PHOTO_MISSING_LABEL: 'Photo steps must have a non-empty report label.',
  WF_ACTION_NOT_OBJECT: 'Each action must be an object.',
  WF_ACTION_UNKNOWN_TYPE: 'Unknown action type.',
  WF_ACTION_INVALID_SHAPE: 'Action is missing required fields or points to an unknown step.',
  WF_STEPS_TOO_LARGE: 'Workflow exceeds the maximum allowed steps or actions.',
  WF_DUPLICATE_NAV_LINK: 'Duplicate navigation links with the same label to the same step.',
  WF_DUPLICATE_NAV_LABEL: 'Multiple navigation buttons share the same label on this step.',
  WF_SHAPE_INVALID: 'Workflow step shape is invalid.',
};

const ALLOWED_STEP_TYPES = new Set(['html', 'form', 'photo', 'start', 'default']);
const ALLOWED_ACTION_TYPES = new Set(['navigation', 'external', 'apicall']);
const STEP_ID_FORMAT = /^[a-zA-Z][a-zA-Z0-9_-]{0,63}$/;
const MAX_STEPS = 200;
const MAX_ACTIONS_PER_STEP = 20;

function stepId(step) {
  if (!step || typeof step !== 'object') return '';
  const id = step.id;
  return typeof id === 'string' && id.trim() !== '' ? id.trim() : '';
}

function isFormStep(step) {
  const t = (step?.type || '').toString().toLowerCase();
  return t === 'form';
}

function isPhotoStep(step) {
  const t = (step?.type || '').toString().toLowerCase();
  return t === 'photo';
}

function navigationLabelKey(action) {
  if (action?.text == null) return '';
  return String(action.text).trim();
}

// Record separator — must not be U+0000 (PostgreSQL rejects null in JSONB text).
const NAV_DUP_KEY_SEP = '\u001e';

function navigationDupKey(target, action) {
  return `${target}${NAV_DUP_KEY_SEP}${navigationLabelKey(action)}`;
}

function navigationActions(step) {
  const actions = step?.actions;
  if (!Array.isArray(actions)) return [];
  return actions.filter((a) => a && typeof a === 'object' && String(a.type).toLowerCase() === 'navigation');
}

function buildAdjacency(steps) {
  const adj = new Map();
  steps.forEach((step) => {
    const sid = stepId(step);
    if (!sid) return;
    const list = [];
    navigationActions(step).forEach((a) => {
      const link = a.link;
      if (link != null && String(link).trim() !== '') list.push(String(link));
    });
    adj.set(sid, list);
  });
  return adj;
}

function bfsFrom(startId, adjacency) {
  const seen = new Set();
  const q = [];
  if (!startId) return seen;
  seen.add(startId);
  q.push(startId);
  while (q.length) {
    const cur = q.shift();
    const outs = adjacency.get(cur) || [];
    outs.forEach((nxt) => {
      if (!seen.has(nxt)) {
        seen.add(nxt);
        q.push(nxt);
      }
    });
  }
  return seen;
}

function pushViolation(out, { code, message, path, context, severity = 'error' }) {
  out.push({
    code,
    message: message || VIOLATION_MESSAGES[code] || 'Validation issue',
    path,
    context: context || {},
    severity,
  });
}

/**
 * @param {unknown} steps - workflow_steps
 * @param {{ knownFormSlugs?: Set<string>, formsLoading?: boolean, mode?: 'draft' | 'publish' }} [options]
 * @returns {{ code: string, message: string, path?: (string|number)[], context?: object, severity: string }[]}
 */
export function validateWorkflowPreflight(steps, options = {}) {
  const { knownFormSlugs = new Set(), formsLoading = false, mode = 'publish' } = options;
  const graphSeverity = mode === 'draft' ? 'warning' : 'error';
  const out = [];

  if (!Array.isArray(steps)) {
    pushViolation(out, {
      code: 'WF_STEPS_NOT_ARRAY',
      path: ['workflow_steps'],
    });
    return out;
  }

  if (steps.length === 0) {
    pushViolation(out, {
      code: 'WF_STEPS_EMPTY',
      path: ['workflow_steps'],
    });
  }

  if (steps.length > MAX_STEPS) {
    pushViolation(out, {
      code: 'WF_STEPS_TOO_LARGE',
      message: `Workflow must not exceed ${MAX_STEPS} steps.`,
      path: ['workflow_steps'],
      context: { count: steps.length, max: MAX_STEPS },
    });
  }

  const idToIndex = new Map();
  steps.forEach((step, index) => {
    const path = ['workflow_steps', index];
    if (!step || typeof step !== 'object') {
      pushViolation(out, { code: 'WF_STEP_INVALID_ID', path });
      return;
    }
    const id = stepId(step);
    if (!id) {
      pushViolation(out, { code: 'WF_STEP_INVALID_ID', path });
      return;
    }
    if (!STEP_ID_FORMAT.test(id)) {
      pushViolation(out, {
        code: 'WF_STEP_ID_INVALID_FORMAT',
        message: `${VIOLATION_MESSAGES.WF_STEP_ID_INVALID_FORMAT} (${id})`,
        path,
        context: { step_id: id },
      });
    }
    if (idToIndex.has(id)) {
      pushViolation(out, {
        code: 'WF_DUPLICATE_STEP_ID',
        message: `${VIOLATION_MESSAGES.WF_DUPLICATE_STEP_ID} (${id})`,
        path,
        context: { step_id: id },
      });
    } else {
      idToIndex.set(id, index);
    }

    const stepType = (step.type || '').toString().toLowerCase();
    if (step.type && !ALLOWED_STEP_TYPES.has(stepType)) {
      pushViolation(out, {
        code: 'WF_STEP_UNKNOWN_TYPE',
        message: `${VIOLATION_MESSAGES.WF_STEP_UNKNOWN_TYPE} (${step.type}, step ${id})`,
        path: ['workflow_steps', index, 'type'],
        context: { step_id: id, type: String(step.type) },
      });
    }
  });

  const startStepIndexes = steps
    .map((step, index) => ({ step, index }))
    .filter(({ step }) => stepId(step) === 'start')
    .map(({ index }) => index);

  if (startStepIndexes.length === 0) {
    pushViolation(out, {
      code: 'WF_START_MISSING',
      path: ['workflow_steps'],
    });
  } else if (startStepIndexes.length > 1) {
    startStepIndexes.forEach((idx) => {
      pushViolation(out, {
        code: 'WF_START_DUPLICATE',
        path: ['workflow_steps', idx],
      });
    });
  }

  const idSet = new Set(idToIndex.keys());

  steps.forEach((step, sIndex) => {
    if (!step || typeof step !== 'object') return;
    const sid = stepId(step);
    if (!sid) return;

    const actions = step.actions;
    if (actions == null || actions === '') return;

    if (!Array.isArray(actions)) {
      pushViolation(out, {
        code: 'WF_NAV_INVALID_LINK',
        message: `${VIOLATION_MESSAGES.WF_NAV_INVALID_LINK} (step ${sid})`,
        path: ['workflow_steps', sIndex, 'actions'],
        context: { step_id: sid },
      });
      return;
    }

    if (actions.length > MAX_ACTIONS_PER_STEP) {
      pushViolation(out, {
        code: 'WF_STEPS_TOO_LARGE',
        message: `Step ${sid} must not exceed ${MAX_ACTIONS_PER_STEP} actions.`,
        path: ['workflow_steps', sIndex, 'actions'],
        context: { step_id: sid, count: actions.length, max: MAX_ACTIONS_PER_STEP },
      });
    }

    const navLabelGroups = new Map();
    const navByLabel = new Map();
    actions.forEach((action, aIndex) => {
      const path = ['workflow_steps', sIndex, 'actions', aIndex];
      if (!action || typeof action !== 'object') {
        pushViolation(out, {
          code: 'WF_ACTION_NOT_OBJECT',
          message: `${VIOLATION_MESSAGES.WF_ACTION_NOT_OBJECT} (step ${sid})`,
          path,
          context: { step_id: sid },
        });
        return;
      }

      const atype = String(action.type || '');
      const atypeKey = atype.toLowerCase();
      if (atype && !ALLOWED_ACTION_TYPES.has(atypeKey)) {
        pushViolation(out, {
          code: 'WF_ACTION_UNKNOWN_TYPE',
          message: `${VIOLATION_MESSAGES.WF_ACTION_UNKNOWN_TYPE} (${atype}, step ${sid})`,
          path,
          context: { step_id: sid, action_type: atype },
          severity: 'warning',
        });
      }

      if (atypeKey === 'navigation') {
        const link = action.link;
        if (link == null || String(link).trim() === '') {
          pushViolation(out, {
            code: 'WF_NAV_INVALID_LINK',
            message: `${VIOLATION_MESSAGES.WF_NAV_INVALID_LINK} (step ${sid})`,
            path,
            context: { step_id: sid },
          });
        } else {
          const target = String(link);
          const label = navigationLabelKey(action);
          const dupKey = navigationDupKey(target, action);
          if (!navLabelGroups.has(dupKey)) navLabelGroups.set(dupKey, []);
          navLabelGroups.get(dupKey).push(aIndex);
          if (!navByLabel.has(label)) navByLabel.set(label, []);
          navByLabel.get(label).push({ actionIndex: aIndex, target });
          if (!idSet.has(target)) {
            pushViolation(out, {
              code: 'WF_NAV_UNKNOWN_TARGET',
              message: `${VIOLATION_MESSAGES.WF_NAV_UNKNOWN_TARGET} (${sid} → ${target})`,
              path,
              context: { step_id: sid, link: target },
            });
          }
        }
      } else if (atypeKey === 'external') {
        if (action.link == null || String(action.link).trim() === '') {
          pushViolation(out, {
            code: 'WF_ACTION_INVALID_SHAPE',
            message: `${VIOLATION_MESSAGES.WF_ACTION_INVALID_SHAPE} (external on step ${sid})`,
            path,
            context: { step_id: sid, action_type: 'external' },
          });
        }
      } else if (atypeKey === 'apicall') {
        if (action.link == null || String(action.link).trim() === '') {
          pushViolation(out, {
            code: 'WF_ACTION_INVALID_SHAPE',
            message: `${VIOLATION_MESSAGES.WF_ACTION_INVALID_SHAPE} (apiCall on step ${sid})`,
            path,
            context: { step_id: sid, action_type: 'apiCall' },
          });
        }
        ['onSuccess', 'onError'].forEach((field) => {
          const target = action[field];
          if (target == null || String(target).trim() === '') return;
          const targetId = String(target);
          if (!idSet.has(targetId)) {
            pushViolation(out, {
              code: 'WF_ACTION_INVALID_SHAPE',
              message: `${VIOLATION_MESSAGES.WF_ACTION_INVALID_SHAPE} (apiCall ${field} on step ${sid} → ${targetId})`,
              path: [...path, field],
              context: { step_id: sid, link: targetId, field },
            });
          }
        });
      }
    });

    navLabelGroups.forEach((actionIndexes, dupKey) => {
      if (actionIndexes.length < 2) return;
      const separatorIndex = dupKey.indexOf(NAV_DUP_KEY_SEP);
      const target = dupKey.slice(0, separatorIndex);
      const label = dupKey.slice(separatorIndex + 1);

      actionIndexes.forEach((aIndex) => {
        const labelSuffix = label ? `, label "${label}"` : '';
        pushViolation(out, {
          code: 'WF_DUPLICATE_NAV_LINK',
          message: `${VIOLATION_MESSAGES.WF_DUPLICATE_NAV_LINK} (${sid} → ${target}${labelSuffix})`,
          path: ['workflow_steps', sIndex, 'actions', aIndex],
          context: { step_id: sid, link: target, action_index: aIndex, label },
          severity: 'warning',
        });
      });
    });

    navByLabel.forEach((entries, label) => {
      if (entries.length < 2) return;

      entries.forEach((entry) => {
        const hasOtherTarget = entries.some(
          (other) =>
            other.actionIndex !== entry.actionIndex && other.target !== entry.target
        );
        if (!hasOtherTarget) return;

        const labelDetail = label ? `label "${label}"` : 'unlabeled buttons';
        pushViolation(out, {
          code: 'WF_DUPLICATE_NAV_LABEL',
          message: `${VIOLATION_MESSAGES.WF_DUPLICATE_NAV_LABEL} (${sid}, ${labelDetail}, → ${entry.target})`,
          path: ['workflow_steps', sIndex, 'actions', entry.actionIndex],
          context: {
            step_id: sid,
            link: entry.target,
            action_index: entry.actionIndex,
            label,
          },
          severity: 'warning',
        });
      });
    });
  });

  if (idSet.has('start')) {
    const adj = buildAdjacency(steps);
    const reachable = bfsFrom('start', adj);
    idSet.forEach((nodeId) => {
      if (!reachable.has(nodeId)) {
        const idx = idToIndex.get(nodeId);
        pushViolation(out, {
          code: 'WF_UNREACHABLE_STEP',
          message: `${VIOLATION_MESSAGES.WF_UNREACHABLE_STEP} (${nodeId})`,
          path: typeof idx === 'number' ? ['workflow_steps', idx] : ['workflow_steps'],
          context: { step_id: nodeId },
          severity: graphSeverity,
        });
      }
    });
  }

  steps.forEach((step, index) => {
    if (!step || typeof step !== 'object') return;
    if (!isFormStep(step)) return;
    const sid = stepId(step);
    const slug = (step.slug != null ? String(step.slug) : '').trim();

    if (!slug && step.form_definition_id != null) {
      pushViolation(out, {
        code: 'WF_FORM_LEGACY_ID',
        message: `${VIOLATION_MESSAGES.WF_FORM_LEGACY_ID} (step ${sid})`,
        path: ['workflow_steps', index, 'form_definition_id'],
        context: { step_id: sid, form_definition_id: step.form_definition_id },
        severity: graphSeverity,
      });
      return;
    }

    if (!slug) {
      pushViolation(out, {
        code: 'WF_FORM_MISSING_SLUG',
        message: `${VIOLATION_MESSAGES.WF_FORM_MISSING_SLUG} (${sid})`,
        path: ['workflow_steps', index, 'slug'],
        context: { step_id: sid },
        severity: graphSeverity,
      });
      return;
    }

    if (!formsLoading && !knownFormSlugs.has(slug)) {
      pushViolation(out, {
        code: 'WF_FORM_UNKNOWN_SLUG',
        message: `${VIOLATION_MESSAGES.WF_FORM_UNKNOWN_SLUG} (${slug}, step ${sid})`,
        path: ['workflow_steps', index, 'slug'],
        context: { slug, step_id: sid },
        severity: graphSeverity,
      });
    }
  });

  steps.forEach((step, index) => {
    if (!step || typeof step !== 'object') return;
    if (!isPhotoStep(step)) return;
    const sid = stepId(step);
    const label = (step.label != null ? String(step.label) : '').trim();

    if (!label) {
      pushViolation(out, {
        code: 'WF_PHOTO_MISSING_LABEL',
        message: `${VIOLATION_MESSAGES.WF_PHOTO_MISSING_LABEL} (${sid})`,
        path: ['workflow_steps', index, 'label'],
        context: { step_id: sid },
        severity: graphSeverity,
      });
    }
  });

  return out;
}

export function hasBlockingPreflightErrors(violations) {
  return (violations || []).some((v) => (v.severity || 'error') === 'error');
}

export function violationDedupeKey(item) {
  if (!item?.code) return JSON.stringify(item);

  const ctx = item.context || {};
  const parts = [item.code];
  if (ctx.step_id) parts.push(`sid:${ctx.step_id}`);
  if (ctx.link) parts.push(`link:${ctx.link}`);
  if (typeof ctx.action_index === 'number') parts.push(`ai:${ctx.action_index}`);
  if (parts.length === 1) {
    parts.push(JSON.stringify(item.path || []));
  }
  return parts.join('|');
}

export function mergeValidationDetails(localViolations, serverDetails) {
  const seen = new Set();
  const merged = [];

  [...(localViolations || []), ...(serverDetails || [])].forEach((item) => {
    const key = violationDedupeKey(item);
    if (seen.has(key)) return;
    seen.add(key);
    merged.push(item);
  });

  return merged;
}

export function getViolationMessage(violation) {
  if (!violation) return 'Validation issue';
  return violation.message || VIOLATION_MESSAGES[violation.code] || 'Validation issue';
}

function emptyStepValidationEntry() {
  return { errors: [], warnings: [] };
}

function addStepIssue(map, stepId, message, severity) {
  if (!stepId) return;
  const entry = map.get(stepId) || emptyStepValidationEntry();
  const bucket = severity === 'warning' ? 'warnings' : 'errors';
  if (!entry[bucket].includes(message)) {
    entry[bucket].push(message);
  }
  map.set(stepId, entry);
}

/**
 * @returns {Map<string, { errors: string[], warnings: string[] }>}
 */
export function getStepValidationMap(violations, steps) {
  const map = new Map();

  (violations || []).forEach((violation) => {
    const severity = violation.severity || 'error';
    const message = getViolationMessage(violation);
    const stepIdsForViolation = new Set();

    if (violation.context?.step_id) {
      stepIdsForViolation.add(violation.context.step_id);
      addStepIssue(map, violation.context.step_id, message, severity);
    }

    const path = violation.path;
    if (Array.isArray(path) && path[0] === 'workflow_steps' && typeof path[1] === 'number') {
      const step = steps?.[path[1]];
      if (step?.id && !stepIdsForViolation.has(step.id)) {
        addStepIssue(map, step.id, message, severity);
      }
    }

    if (violation.code === 'WF_NAV_UNKNOWN_TARGET' && violation.context?.link) {
      addStepIssue(map, violation.context.link, message, severity);
    }
  });

  return map;
}

function emptyEdgeValidationEntry() {
  return { errors: [], warnings: [] };
}

function resolveEdgeActionIndex(violation) {
  if (typeof violation?.context?.action_index === 'number') {
    return violation.context.action_index;
  }
  const path = violation?.path;
  if (
    Array.isArray(path) &&
    path[0] === 'workflow_steps' &&
    path[2] === 'actions' &&
    typeof path[3] === 'number'
  ) {
    return path[3];
  }
  return undefined;
}

function addEdgeIssue(map, source, target, message, severity, actionIndex) {
  if (!source || !target) return;
  const key =
    typeof actionIndex === 'number'
      ? buildEdgeId(source, target, actionIndex)
      : `${source}->${target}`;
  const entry = map.get(key) || emptyEdgeValidationEntry();
  const bucket = severity === 'warning' ? 'warnings' : 'errors';
  if (!entry[bucket].includes(message)) {
    entry[bucket].push(message);
  }
  map.set(key, entry);
}

const EDGE_RELATED_CODES = new Set([
  'WF_NAV_INVALID_LINK',
  'WF_NAV_UNKNOWN_TARGET',
  'WF_DUPLICATE_NAV_LINK',
  'WF_DUPLICATE_NAV_LABEL',
  'WF_ACTION_INVALID_SHAPE',
  'WF_ACTION_NOT_OBJECT',
  'WF_ACTION_UNKNOWN_TYPE',
]);

/**
 * @returns {Map<string, { errors: string[], warnings: string[] }>}
 */
export function getEdgeValidationMap(violations) {
  const map = new Map();

  (violations || []).forEach((violation) => {
    if (!EDGE_RELATED_CODES.has(violation.code)) return;

    const source = violation.context?.step_id;
    const target = violation.context?.link;
    if (!source || !target) return;

    const actionIndex = resolveEdgeActionIndex(violation);
    addEdgeIssue(
      map,
      source,
      target,
      getViolationMessage(violation),
      violation.severity || 'error',
      actionIndex
    );
  });

  return map;
}

function resolvePrimaryStepId(violation, steps) {
  if (violation?.context?.step_id) return violation.context.step_id;

  const path = violation?.path;
  if (Array.isArray(path) && path[0] === 'workflow_steps' && typeof path[1] === 'number') {
    return steps?.[path[1]]?.id || null;
  }

  return null;
}

/**
 * @returns {{ type: 'node', id: string } | { type: 'edge', source: string, target: string, actionIndex?: number, edgeId?: string } | null}
 */
export function getViolationFocusTarget(violation, steps) {
  if (!violation) return null;

  const source = violation.context?.step_id;
  const target = violation.context?.link;

  if (source && target && EDGE_RELATED_CODES.has(violation.code)) {
    const actionIndex = resolveEdgeActionIndex(violation);
    const edgeId =
      typeof actionIndex === 'number' ? buildEdgeId(source, target, actionIndex) : undefined;
    return { type: 'edge', source, target, actionIndex, edgeId };
  }

  const stepId = resolvePrimaryStepId(violation, steps);
  if (stepId) {
    return { type: 'node', id: stepId };
  }

  if (target) {
    return { type: 'node', id: target };
  }

  return null;
}

export function stepValidationToNodeData(entry) {
  if (!entry) {
    return {
      hasValidationError: false,
      hasValidationWarning: false,
      validationMessages: [],
      validationErrorCount: 0,
    };
  }

  const errors = entry.errors || [];
  const warnings = entry.warnings || [];

  return {
    hasValidationError: errors.length > 0,
    hasValidationWarning: warnings.length > 0,
    validationMessages: [...errors, ...warnings],
    validationErrorCount: errors.length + warnings.length,
  };
}
